import React from 'react'
import { useTranslation } from 'react-i18next'

import { useApi } from '@core/api'
import {
  CreateExternalMetadataConfigInput,
  ExternalMetadataConfigOutput,
  OptionsSettingSchema,
  UpdateExternalMetadataConfigInput,
} from '@core/api/generated'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

type UnifiedMetadataConfig = {
  legacy: OptionsSettingSchema[] | null
  config: ExternalMetadataConfigOutput | null
}

const externalMetadataKeys = {
  all: ['external-metadata-config'] as const,
  detail: (configId: string) => [...externalMetadataKeys.all, configId] as const,
  byModel: (modelId: string) => [...externalMetadataKeys.all, 'models', modelId] as const,
  byItem: (itemId: string) => [...externalMetadataKeys.all, 'items', itemId] as const,
}

export const useGetMetadataConfigByModel = (modelId: string | null | undefined) => {
  const api = useApi()

  return useQuery({
    queryKey: externalMetadataKeys.byModel(modelId ?? ''),
    queryFn: async () => {
      if (!modelId) return null
      const { data } = await api.getMetadataConfigByModel({
        path: { model_id: modelId },
        throwOnError: true,
      })
      return data as UnifiedMetadataConfig
    },
    enabled: !!modelId,
  })
}

export const useGetMetadataConfigByItem = (itemId: string | null | undefined) => {
  const api = useApi()

  return useQuery({
    queryKey: externalMetadataKeys.byItem(itemId ?? ''),
    queryFn: async () => {
      if (!itemId) return null
      const { data } = await api.getMetadataConfigByItem({
        path: { item_id: itemId },
        throwOnError: true,
      })
      return data as UnifiedMetadataConfig
    },
    enabled: !!itemId,
  })
}

export const usePrefetchMetadataConfigByItem = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return React.useCallback(
    (itemId: string) => {
      queryClient.prefetchQuery({
        queryKey: externalMetadataKeys.byItem(itemId),
        queryFn: async () => {
          if (!itemId) return null
          const { data } = await api.getMetadataConfigByItem({
            path: { item_id: itemId },
            throwOnError: true,
          })
          return data as UnifiedMetadataConfig
        },
      })
    },
    [api, queryClient],
  )
}

export const useCreateExternalMetadataConfig = () => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: CreateExternalMetadataConfigInput) => {
      const { data } = await api.createExternalMetadataConfig({
        body,
        throwOnError: true,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMetadataKeys.all })
      queryClient.invalidateQueries({ queryKey: ['ai-models-profiles-admin'] })
      addSnack({ message: t('config_created'), severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}

export const useUpdateExternalMetadataConfig = () => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      configId,
      body,
    }: {
      configId: string
      body: UpdateExternalMetadataConfigInput
    }) => {
      const { data } = await api.updateExternalMetadataConfig({
        path: { config_id: configId },
        body,
        throwOnError: true,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMetadataKeys.all })
      queryClient.invalidateQueries({ queryKey: ['ai-models-profiles-admin'] })
      addSnack({ message: t('config_updated'), severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}

export const useMigrateLegacyConfig = () => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (modelId: string) => {
      const { data } = await api.migrateLegacyConfig({
        path: { model_id: modelId },
        throwOnError: true,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalMetadataKeys.all })
      queryClient.invalidateQueries({ queryKey: ['ai-models-profiles-admin'] })
      addSnack({ message: t('legacy_migrated'), severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}
